import { uploadJobPDF, savePDFUrlToJob } from '../services/pdf-storage';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ═══════════════════════════════════════════════════════════════════════════════
// TWP GLASS ORDER PDF — SINGLE JOB — LANDSCAPE
// Manufacturer: Tracy Corners IG
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND_BROWN = [139,  69,  19];
const BRAND_LIGHT = [200, 160, 110];
const WHITE       = [255, 255, 255];
const LIGHT_GRAY  = [240, 240, 240];
const MID_GRAY    = [180, 180, 180];
const DARK        = [ 40,  40,  40];
const LABEL_COLOR = [100,  60,  20];
const BLUE_BG     = [232, 244, 255];
const BLUE_TEXT   = [ 20,  60, 120];
const MFG_GREEN   = [ 20,  90,  45];
const MFG_BG      = [220, 245, 228];
const RED_TEXT    = [180,  30,  30];
const RED_BG      = [255, 240, 240];

const PAGE_W   = 279;
const PAGE_H   = 216;
const MARGIN   = 14;
const HEADER_H = 38;
const FOOTER_H = 18;

const STANDARD_OA = '7/8"';

const TLA_CONFIGS  = ['TLA', 'TDLA', 'TLK', 'TDC', 'TDCA'];
const SG_CONFIGS   = ['SG'];
const SPECIAL_OPTS = ['obscure', 'gray tint', 'bronze tint', 'tempered'];

const CONFIG_LABELS = {
  TLA:  'Triple Low-E Argon',
  TDLA: 'Triple Double Low-E Argon',
  TLK:  'Triple Low-E Krypton',
  TDC:  'Triple Double Clear',
  TDCA: 'Triple Double Clear Argon',
  DLA:  'Double Low-E Argon',
  DCA:  'Double Clear Argon',
  DC:   'Double Clear',
  SG:   'Single Pane Glass',
};

// ── Grid type label ───────────────────────────────────────────────────────────
// Returns exactly 'Flat', 'Contoured', or '' (empty = no grids).
// Falls back to 'Yes' only if gridType field is completely absent.
function gridTypeLabel(pane) {
  if (!pane.grids) return '';
  const hasQty = parseInt(pane.gridQtyV) > 0 || parseInt(pane.gridQtyH) > 0;
  if (!hasQty) return '';
  const t = (pane.gridType || '').toLowerCase().trim();
  if (t === 'flat')      return 'Flat';
  if (t === 'contoured') return 'Contoured';
  if (t)                 return pane.gridType; // pass through any other stored value
  return 'Yes';                                // safe fallback if field absent
}

function isSpecialOption(opt) {
  return SPECIAL_OPTS.some(o => (opt || '').toLowerCase().includes(o));
}
function isNonStandardOA(oaThick) {
  return oaThick && oaThick.trim() !== STANDARD_OA;
}
function isSpecialRow(row) {
  const pkg = (row.glassType || '').toUpperCase().trim();
  return TLA_CONFIGS.includes(pkg)
    || SG_CONFIGS.includes(pkg)
    || !!row.grids
    || isSpecialOption(row.glassConfig)
    || isNonStandardOA(row.oaThick);
}
function specialRowReason(row) {
  const pkg = (row.glassType || '').toUpperCase().trim();
  const r   = [];
  if (TLA_CONFIGS.includes(pkg) || SG_CONFIGS.includes(pkg))
    r.push(`${pkg} — ${CONFIG_LABELS[pkg] || pkg}`);
  if (row.grids) {
    const gc = (row.gridColor || '').trim();
    const gcNote = (gc && gc.toLowerCase() !== 'white') ? ` (${gc})` : '';
    r.push(`Grids: ${row.grids}${gcNote}`);
  }
  if (isSpecialOption(row.glassConfig)) r.push(`Option: ${row.glassConfig}`);
  if (isNonStandardOA(row.oaThick))   r.push(`OA Thick: ${row.oaThick}`);
  return r.join(' + ');
}

// ── Attention notice ──────────────────────────────────────────────────────────
function drawAttentionNotice(doc, lineInfos, noticeY) {
  if (!lineInfos.length) return noticeY;
  const rowH = 6, padV = 3;
  const noticeX = MARGIN, noticeW = PAGE_W - MARGIN * 2;
  const noticeH = padV * 2 + lineInfos.length * rowH;
  doc.setFillColor(...MFG_BG); doc.setDrawColor(...MFG_GREEN); doc.setLineWidth(0.4);
  doc.roundedRect(noticeX, noticeY, noticeW, noticeH, 1.5, 1.5, 'FD');
  doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...MFG_GREEN);
  const attnTxt = 'ATTENTION:';
  const attnW   = doc.getTextWidth(attnTxt);
  doc.text(attnTxt, noticeX + 5, noticeY + padV + 3.5);
  const entryX = noticeX + 5 + attnW + 3;
  lineInfos.forEach((info, i) => {
    const lineY = noticeY + padV + i * rowH + 3.5;
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...MFG_GREEN);
    const tag = `Line ${info.lineNum}`;
    doc.text(tag, entryX, lineY);
    const tagW = doc.getTextWidth(tag);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    const lbl = `  (${info.label})`;
    doc.text(lbl, entryX + tagW, lineY);
    const lblW = doc.getTextWidth(lbl);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...MFG_GREEN);
    doc.text(`  —  ${info.reason}`, entryX + tagW + lblW, lineY);
    if (i < lineInfos.length - 1) {
      doc.setDrawColor(...MFG_GREEN); doc.setLineWidth(0.15);
      doc.line(noticeX + 3, noticeY + padV + (i + 1) * rowH,
               noticeX + noticeW - 3, noticeY + padV + (i + 1) * rowH);
    }
  });
  doc.setLineWidth(0.3); doc.setTextColor(...DARK); doc.setDrawColor(...LIGHT_GRAY);
  return noticeY + noticeH + 3;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function toFraction(val) {
  if (val == null || val === '') return '—';
  const raw = parseFloat(val);
  if (isNaN(raw)) return String(val);
  const n       = Math.round(raw * 16) / 16;
  const whole   = Math.floor(n);
  const decimal = n - whole;
  if (Math.abs(decimal) < 0.001) return String(whole);
  const cf = [
    [0.0625,'1/16'],[0.125,'1/8'],[0.1875,'3/16'],[0.25,'1/4'],[0.3125,'5/16'],
    [0.375,'3/8'],[0.4375,'7/16'],[0.5,'1/2'],[0.5625,'9/16'],[0.625,'5/8'],
    [0.6875,'11/16'],[0.75,'3/4'],[0.8125,'13/16'],[0.875,'7/8'],[0.9375,'15/16'],
  ];
  for (const [d, f] of cf) {
    if (Math.abs(decimal - d) < 0.001) return whole === 0 ? f : `${whole} ${f}`;
  }
  return n.toFixed(4);
}
function dim(val)  { return toFraction(val); }
function evenUp(n) { return Math.ceil(n / 2) * 2; }

function calcSqFt(w, h) {
  const wn = parseFloat(w), hn = parseFloat(h);
  if (isNaN(wn) || isNaN(hn) || wn <= 0 || hn <= 0) return '';
  const raw = (wn * hn) / 144;
  if (raw < 0.01) return '';
  return String(Math.max(3, parseFloat(((evenUp(wn) * evenUp(hn)) / 144).toFixed(2))));
}
function calcGlassTypeDsTs(w, h) {
  const wn = parseFloat(w), hn = parseFloat(h);
  if (isNaN(wn) || isNaN(hn)) return '—';
  return (wn * hn / 144) <= 20 ? 'DS' : 'TS';
}
function deriveGasFill(pkg) {
  if (!pkg) return '—';
  const c = pkg.toUpperCase().trim();
  if (['DCA','DLA','TLA','TDCA','TDLA'].includes(c)) return 'Argon';
  if (c === 'TLK')              return 'Krypton';
  if (['DC','TDC'].includes(c)) return 'No Gas';
  if (c === 'SG')               return '—';
  return 'Argon';
}
function paneSuffix(paneLabel) {
  if (!paneLabel) return '';
  const l = paneLabel.toLowerCase();
  if (l.includes('center'))    return 'CC';
  if (l.includes('keeper'))    return 'K';
  if (l.includes('lock'))      return 'L';
  if (l.includes('casement'))  return 'C';
  if (l.includes('slider'))    return 'S';
  if (l.includes('picture'))   return 'P';
  if (l.includes('awning'))    return 'A';
  if (l.includes('basement'))  return 'B';
  if (l.includes('transom'))   return 'T';
  if (l.includes('sidelight')) return 'SL';
  if (l.includes('fixed'))     return 'F';
  if (l.includes('upper'))     return 'U';
  if (l.includes('lower'))     return 'LW';
  if (l.includes('bay'))       return 'BY';
  return paneLabel.slice(0, 2).toUpperCase();
}

// ── Logo ──────────────────────────────────────────────────────────────────────
const LOGO_URL = '/brand/twp-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror   = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── Header / Footer ───────────────────────────────────────────────────────────
function drawHeader(doc, job, pageNum, totalPages, logo) {
  doc.setFillColor(...WHITE); doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);  doc.line(0, 0, 0, HEADER_H);
  doc.setLineWidth(0.8); doc.line(0, HEADER_H, PAGE_W, HEADER_H);
  doc.setLineWidth(0.3);
  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN, 4, 18, 18); } catch {} }
  doc.setTextColor(...BRAND_BROWN); doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 22, 12);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('1300 E 4th Ave  •  Milbank, SD 57252  •  (605) 432-1834  •  thewindowplacellc.com', MARGIN + 22, 17.5);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BLUE_TEXT);
  doc.text('GLASS ORDER', PAGE_W - MARGIN, 14, { align: 'right' });
  doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
  doc.line(MARGIN, 22, PAGE_W - MARGIN, 22);
  const fields = [
    ['Customer', job.customerName || '—'],
    ['WO #',    String(job.woNumber || '—')],
    ['Date',    job.orderDate
      ? new Date(job.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '—'],
    ['Page',    `${pageNum} of ${totalPages}`],
  ];
  let fx = MARGIN;
  fields.forEach(([label, value]) => {
    doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...LABEL_COLOR);
    doc.text(label + ':', fx, 28);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    doc.text(value, fx, 33);
    fx += 55;
  });
  const mfgTxt = 'Mfg: Tracy Corners IG';
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
  const mw = doc.getTextWidth(mfgTxt) + 9;
  const mx = PAGE_W - MARGIN - mw / 2 - 2, my = 24;
  doc.setFillColor(...MFG_BG);
  doc.roundedRect(mx - mw / 2, my, mw, 7, 1.5, 1.5, 'F');
  doc.setDrawColor(...MFG_GREEN); doc.setLineWidth(0.35);
  doc.roundedRect(mx - mw / 2, my, mw, 7, 1.5, 1.5, 'S');
  doc.setTextColor(...MFG_GREEN);
  doc.text(mfgTxt, mx, my + 5, { align: 'center' });
}

function drawFooter(doc, totalRows, totalSqFt) {
  const y = PAGE_H - FOOTER_H;
  doc.setFillColor(...WHITE); doc.rect(0, y, PAGE_W, FOOTER_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);  doc.line(0, y, 0, PAGE_H);
  doc.setLineWidth(0.8); doc.line(0, y, PAGE_W, y);
  doc.setLineWidth(0.3);
  doc.setTextColor(...LABEL_COLOR); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
  doc.text(
    'The Window Place LLC  •  1300 E 4th Ave, Milbank, SD 57252  •  (605) 432-1834  •  office@thewindowplacellc.com',
    PAGE_W / 2, y + 4.5, { align: 'center' }
  );
  const now     = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + '  ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...MID_GRAY); doc.setFontSize(6.5);
  doc.text(
    `Total Lines: ${totalRows}  •  Total Sq Ft: ${totalSqFt}  •  Printed: ${printed}`,
    PAGE_W / 2, y + 9, { align: 'center' }
  );
  // ── Grid color spec note — helps Tracy Corners understand the column ──
  doc.setTextColor(...MFG_GREEN); doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
  doc.text(
    'Grid spec: outside face is always WHITE. Inside face matches the INSIDE GRID COLOR shown per line.',
    PAGE_W / 2, y + 13, { align: 'center' }
  );
  doc.setFont('helvetica', 'normal');
}

// ── Columns ───────────────────────────────────────────────────────────────────
// Total = 248 mm  (content area = 279 - 14×2 = 251 mm — fits with 3 mm to spare)
//
// Key widths justified:
//   GAS FILL  17 mm — "Krypton" is the longest value (~16 mm at font 8)
//   GRID TYPE 19 mm — "Contoured" is the longest value (~18 mm at font 8)
//   GRID PATTERN 20 mm — "Colonial" needs ~17 mm; 20 mm gives breathing room
//   GRID COLOR   20 mm — "dk woodgrain" needs ~19 mm at font 8
//   VERT / HORI   8 mm — single/double digit numbers only
const COLS = [
  { header: 'QTY',           dataKey: 'qty',         width: 8  },
  { header: 'WIDTH',         dataKey: 'width',        width: 22 },
  { header: 'HEIGHT',        dataKey: 'height',       width: 22 },
  { header: 'LABEL',         dataKey: 'label',        width: 20 },
  { header: 'GLASS\nTYPE',  dataKey: 'dsTs',         width: 11 },
  { header: 'GLASS\nCONFIG',dataKey: 'glassType',    width: 13 },
  { header: 'GAS FILL',      dataKey: 'gasFill',      width: 17 }, // fits Krypton
  { header: 'OPTIONS',       dataKey: 'glassConfig',  width: 20 },
  { header: 'GRID\nTYPE',   dataKey: 'grids',        width: 19 }, // fits Contoured
  { header: 'GRID\nPATTERN',dataKey: 'gridPattern',  width: 20 }, // fits Colonial
  { header: 'VERT',          dataKey: 'gridV',        width: 8  },
  { header: 'HORI',          dataKey: 'gridH',        width: 8  },
  { header: 'INSIDE GRID\nCOLOR',  dataKey: 'gridColor',    width: 28 }, // fits 'Dk Woodgrain' at font 8
  { header: 'OA\nTHICK',    dataKey: 'oaThick',      width: 12 },
  { header: 'AIR\nSPACE',   dataKey: 'airSpace',     width: 11 },
  { header: 'SQ FT',         dataKey: 'sqft',         width: 11 },
]; // total = 242 mm ✓

const OPTIONS_COL      = 7;
const GRIDS_COL        = 8;
const GRID_PATTERN_COL = 9;
const OA_THICK_COL     = 13;
const GLASS_CFG_COL    = 5;

const COL_STYLES = {};
COLS.forEach((c, i) => { COL_STYLES[i] = { cellWidth: c.width, halign: 'center' }; });
// Bold/coloured columns
COL_STYLES[3]  = { cellWidth: COLS[3].width,  halign: 'center', fontStyle: 'bold', textColor: LABEL_COLOR };
COL_STYLES[4]  = { cellWidth: COLS[4].width,  halign: 'center', fontStyle: 'bold', textColor: BLUE_TEXT };
COL_STYLES[5]  = { cellWidth: COLS[5].width,  halign: 'center', fontStyle: 'bold', textColor: BLUE_TEXT };
COL_STYLES[8]  = { cellWidth: COLS[8].width,  halign: 'center', fontStyle: 'bold' };
COL_STYLES[15] = { cellWidth: COLS[15].width, halign: 'right' };
// Larger font for dimensions
COL_STYLES[1]  = { cellWidth: COLS[1].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };
COL_STYLES[2]  = { cellWidth: COLS[2].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };

// ═══════════════════════════════════════════════════════════════════════════════
export async function generateGlassOrderPDF(job, calculatedWindows, returnBlob = false) {
  const logo = await getLogo();
  const doc  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  // ── Build rows ────────────────────────────────────────────────────────────
  // Collapse identical input windows BEFORE expanding into glass panes,
  // so qty 2 of an identical mulled DH produces one set of pane rows with qty 2,
  // not two duplicate sets.
  const norm = v => (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();
  const nf = v => { const n = parseFloat(v); return isNaN(n) ? '' : String(n); };
  const winKey = w => [
    norm(w.type || w.code || w.series),
    nf(w.overallW || w.width),
    nf(w.height),
    norm(w.color || w.frameColor),
    norm(w.handing),
    JSON.stringify((w.glass || []).map(p => [nf(p.width), nf(p.height), norm(p.glassType), norm(p.glassConfig), norm(p.gridPattern), nf(p.gridQtyV), nf(p.gridQtyH), norm(p.gridColor)])),
  ].join('|');
  const isPW = w => norm(w.type || w.code || w.series).startsWith('pw') || norm(w.series).includes('picture');
  // ROLLUP RULE (matches rollup-windows.js):
  //   Standalone windows NEVER merge with each other — even if winKey matches.
  //   Two same-size standalones may differ in notes, sub-options, tempered
  //   sashes, glass upgrades, or other fields not captured by winKey. Merging
  //   them silently UNDER-orders glass (job 8915: 4 DH + 1 different DH was
  //   merged to qty 5 but pane.qty stayed at 4 → 9 panes instead of 11).
  //
  //   Mulled panes ONLY merge when they share the SAME mulledUnitLabel AND
  //   the same winKey. This correctly handles qty>1 of a mulled assembly
  //   without collapsing across different physical assemblies.
  const mulledGroups   = new Map();
  const standaloneList = []; // pass-through, never merged
  calculatedWindows.forEach((w, i) => {
    const qty = parseFloat(w.qty) || 1;
    if (w.isMulledPane) {
      const label = norm(w.mulledUnitLabel || `__nolabel_${i}`);
      const k = `${label}::${winKey(w)}`;
      if (mulledGroups.has(k)) mulledGroups.get(k).qty += qty;
      else mulledGroups.set(k, { win: { ...w }, qty, firstIdx: i });
    } else {
      standaloneList.push({ win: { ...w }, qty, firstIdx: i });
    }
  });
  let rolledWindows = [...mulledGroups.values(), ...standaloneList]
    .sort((a, b) => a.firstIdx - b.firstIdx)
    .map(g => ({ ...g.win, qty: g.qty }));
  rolledWindows = rolledWindows
    .map((r, i) => ({ r, i, pw: isPW(r) ? 0 : 1 }))
    .sort((a, b) => a.pw - b.pw || a.i - b.i)
    .map(x => x.r);

  const rows = [];
  rolledWindows.forEach(win => {
    if (!win.glass || win.glass.length === 0) return;
    win.glass.forEach(pane => {
      const gridLabel = gridTypeLabel(pane);
      const hasGrids  = !!gridLabel;
      const baseLabel = win.label || String(job.woNumber || '');
      const suffix    = paneSuffix(pane.label || '');
      const mfgLabel  = suffix ? `${baseLabel}${suffix}` : baseLabel;
      const glassPkg  = (pane.glassType   || 'DLA').trim();
      const glassOpt  = (pane.glassConfig || '').trim();
      const sqft      = calcSqFt(pane.width, pane.height);
      rows.push({
        // QTY FIX — pane.qty is ALREADY the final total (qty x lites) from
        // the calc. Do NOT multiply by win.qty again — that squared the
        // count (7 windows -> 49 pieces). Use pane.qty straight.
        qty:         String(parseInt(pane.qty) || 1),
        _qtyNum:     parseInt(pane.qty) || 1,
        width:       dim(pane.width),
        height:      dim(pane.height),
        label:       mfgLabel,
        dsTs:        calcGlassTypeDsTs(pane.width, pane.height),
        glassType:   glassPkg,
        gasFill:     deriveGasFill(glassPkg),
        glassConfig: glassOpt,
        grids:       gridLabel,            // 'Flat', 'Contoured', or ''
        gridPattern: hasGrids ? (pane.gridPattern || '') : '',
        gridV:       hasGrids ? String(pane.gridQtyV || 0) : '',
        gridH:       hasGrids ? String(pane.gridQtyH || 0) : '',
        gridColor:   hasGrids ? (pane.gridColor || '') : '',
        oaThick:     '7/8"',
        airSpace:    '5/8"',
        sqft,
        _sqftNum:    parseFloat(sqft) || 0,
      });
    });
  });

  // ── GLASS COUNT VERIFICATION ALARM ──────────────────────────────────────
  // Independently recompute the expected piece count straight from the calc
  // output (sum of every glass pane's qty) and compare it to the sum of the
  // table rows. If they disagree, something double-counted — BLOCK the PDF.
  {
    const expected = (calculatedWindows || []).reduce((s, w) =>
      s + (w.glass || []).reduce((ps, p) => ps + (parseInt(p.qty) || 0), 0), 0);
    const actual = rows.reduce((s, r) => s + (parseInt(r._qtyNum ?? r.qty) || 0), 0);
    if (expected !== actual) {
      const msg = `GLASS ORDER BLOCKED — piece count mismatch.\n` +
        `Expected ${expected} pieces from the window list, but the order ` +
        `totals ${actual}. Do NOT send this order. Report this to Kwizera.`;
      // window.alert() removed — error is surfaced via the JobApprovalModal in JobOrdersReviewPage
      throw new Error(msg);
    }
  }

  const totalSqFt     = rows.reduce((a, r) => a + r._sqftNum, 0).toFixed(2);
  const ROWS_PER_PAGE = 16;
  const pages         = [];
  for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) pages.push(rows.slice(i, i + ROWS_PER_PAGE));
  if (pages.length === 0) pages.push([]);
  const totalPages = pages.length;

  pages.forEach((pageRows, pageIdx) => {
    if (pageIdx > 0) doc.addPage();
    drawHeader(doc, job, pageIdx + 1, totalPages, logo);
    drawFooter(doc, rows.length, totalSqFt);

    // Attention notice — lineNum is the real global 1-based position
    const pageStartIdx = pageIdx * ROWS_PER_PAGE;
    const lineInfos    = [];
    pageRows.forEach((row, localIdx) => {
      if (isSpecialRow(row)) {
        lineInfos.push({
          lineNum: pageStartIdx + localIdx + 1,
          label:   row.label,
          reason:  specialRowReason(row),
        });
      }
    });

    let tableStartY = HEADER_H + 5;
    if (lineInfos.length > 0) {
      tableStartY = drawAttentionNotice(doc, lineInfos, tableStartY);
    }

    autoTable(doc, {
      startY: tableStartY,
      head:   [COLS.map(c => c.header)],
      body:   pageRows.map(r => COLS.map(c => r[c.dataKey] ?? '')),
      styles: {
        fontSize:    8,
        cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
        textColor:   DARK,
        lineColor:   LIGHT_GRAY,
        lineWidth:   0.2,
        overflow:    'hidden',
        halign:      'center',
      },
      headStyles: {
        fillColor:     BLUE_BG,
        textColor:     BLUE_TEXT,
        fontStyle:     'bold',
        fontSize:      5.5,
        halign:        'center',
        valign:        'middle',
        minCellHeight: 10,
        overflow:      'hidden',
      },
      alternateRowStyles: { fillColor: [246, 251, 255] },
      columnStyles:       COL_STYLES,
      margin:             { left: MARGIN, right: MARGIN, bottom: FOOTER_H + 6 },
      tableLineColor:     LIGHT_GRAY,
      tableLineWidth:     0.2,

      didParseCell(data) {
        if (data.section !== 'body') return;
        const row = pageRows[data.row.index];
        if (!row) return;

        // Bold entire special rows
        if (isSpecialRow(row)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [0, 0, 0];
        }

        // OPTIONS column — blank if None/empty; green if special option
        if (data.column.index === OPTIONS_COL) {
          const opt = row.glassConfig;
          if (!opt || opt === 'None' || opt === '') {
            data.cell.text = [''];
          } else if (isSpecialOption(opt)) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = MFG_GREEN;
            data.cell.styles.fillColor = MFG_BG;
          }
        }

        // GRID TYPE — 'Flat' green, 'Contoured' blue, empty = blank
        if (data.column.index === GRIDS_COL) {
          const gl = row.grids;
          if (!gl) { data.cell.text = ['']; return; }
          if (gl.toLowerCase() === 'flat') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = MFG_GREEN;
          } else if (gl.toLowerCase() === 'contoured') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = BLUE_TEXT;
          }
        }

        // GRID PATTERN — green fill when grids present
        if (data.column.index === GRID_PATTERN_COL && row.grids) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = MFG_GREEN;
          data.cell.styles.fillColor = MFG_BG;
        }

        // GLASS CONFIG — SG = red
        if (data.column.index === GLASS_CFG_COL &&
            SG_CONFIGS.includes((row.glassType || '').toUpperCase().trim())) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = RED_TEXT;
          data.cell.styles.fillColor = RED_BG;
        }

        // OA THICK — non-standard = red
        if (data.column.index === OA_THICK_COL && isNonStandardOA(row.oaThick)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = RED_TEXT;
          data.cell.styles.fillColor = RED_BG;
        }
      },

      willDrawCell(data) {
        if (data.section !== 'body') return;
        const row = pageRows[data.row.index];
        if (!row || !isSpecialRow(row)) return;
        const { x, y, width: cw, height: ch } = data.cell;
        doc.setDrawColor(0, 0, 0); doc.setLineWidth(1.0);
        doc.line(x, y, x + cw, y);
        doc.line(x, y + ch, x + cw, y + ch);
        if (data.column.index === 0) doc.line(x, y, x, y + ch);
        if (data.column.index === COLS.length - 1) doc.line(x + cw, y, x + cw, y + ch);
        doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.2);
      },
    });

    if (pageIdx === totalPages - 1) {
      const finalY = doc.lastAutoTable.finalY + 5;
      doc.setFillColor(...LIGHT_GRAY);
      doc.roundedRect(MARGIN, finalY, 72, 8, 2, 2, 'F');
      doc.setTextColor(...BRAND_BROWN); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(`Total Lines: ${rows.length}`, MARGIN + 4, finalY + 5.5);
      doc.setFillColor(...BLUE_BG);
      doc.roundedRect(MARGIN + 76, finalY, 72, 8, 2, 2, 'F');
      doc.setTextColor(...BLUE_TEXT);
      doc.text(`Total Sq Ft: ${totalSqFt}`, MARGIN + 80, finalY + 5.5);
    }
  });

  const customer     = (job.customerName || 'Customer').replace(/\s+/g, '_');
  const wo           = job.woNumber || 'DRAFT';
  const filename     = `${customer}_WO${wo}_GlassOrder.pdf`;
  const _blob        = doc.output('blob');
  const _contractNum = (job.woNumber || 'DRAFT').replace(/\s+/g, '_');
  uploadJobPDF(_contractNum, 'glassorder', _blob)
    .then(url => savePDFUrlToJob(job.twpJobId || null, job.id || null, 'glassorder', url))
    .catch(e => console.warn('PDF upload skipped:', e));
  if (returnBlob) return doc.output('blob');
  doc.save(filename);
}